import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { isAdmin, useSessionUser, useVisibleNav } from '@/entities/session';
import { routes } from '@/shared/config';
import { useLanguage } from '@/shared/i18n';
import { useThemeMode } from '@/shared/lib';
import type { IconName } from '@/shared/ui';

export type CommandGroup = 'screens' | 'actions';

export interface Command {
  id: string;
  group: CommandGroup;
  label: string;
  /** Secondary text on the right of the row (the nav group, or nothing for actions). */
  sub?: string;
  icon: IconName;
  run: () => void;
}

/** Case- and accent-insensitive contains, so "kln" still finds "Köln-Kalk". */
function normalise(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function filterCommands(commands: Command[], search: string): Command[] {
  const term = normalise(search.trim());
  if (!term) return commands;
  return commands.filter(
    (command) =>
      normalise(command.label).includes(term) || normalise(command.sub ?? '').includes(term),
  );
}

/**
 * Everything the palette can do.
 *
 * Screens come from the same access-filtered navigation the sidebar renders, so the palette can
 * never offer a route the user would be bounced out of. Actions are the global toggles that have
 * no other keyboard route.
 */
export function useCommands(): Command[] {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const groups = useVisibleNav();
  const user = useSessionUser();
  const { language, setLanguage } = useLanguage();
  const { isDark, toggle } = useThemeMode();

  return useMemo(() => {
    const commands: Command[] = groups.flatMap((group) =>
      group.items.map((item) => ({
        id: `screen:${item.key}`,
        group: 'screens' as const,
        label: t(`nav.items.${item.key}`),
        sub: t(`nav.groups.${group.key}`),
        icon: item.icon,
        run: () => navigate(item.path),
      })),
    );

    commands.push({
      id: 'screen:profile',
      group: 'screens',
      label: t('nav.items.profile'),
      sub: t('header.account'),
      icon: 'user',
      run: () => navigate(routes.profile),
    });

    // The login history is only mounted behind the admin-guarded /users router.
    if (isAdmin(user)) {
      commands.push({
        id: 'screen:loginHistory',
        group: 'screens',
        label: t('nav.items.loginHistory'),
        sub: t('header.account'),
        icon: 'history',
        run: () => navigate(routes.loginHistory),
      });
    }

    commands.push(
      {
        id: 'action:theme',
        group: 'actions',
        label: t('header.toggleTheme'),
        icon: isDark ? 'sun' : 'moon',
        run: toggle,
      },
      {
        id: 'action:language',
        group: 'actions',
        label: t('header.languageSwitch'),
        icon: 'globe',
        run: () => setLanguage(language === 'de' ? 'en' : 'de'),
      },
    );

    return commands;
  }, [groups, t, navigate, user, isDark, toggle, language, setLanguage]);
}
